import { motion, useReducedMotion, useScroll } from "framer-motion";
import { useRef } from "react";

import { SanskritTerm } from "./SanskritTerm";
import { ScrollReveal } from "./ScrollReveal";
import { SectionHead } from "./SectionHead";

/* An alpha-wave burst, the shape a 192-channel EEG actually draws —
   calm oscillation, a spindle of amplitude, calm again. */
const EEG_PATH =
  "M0 30 H140 Q150 30 156 22 T172 30 T188 26 T204 30 H320 Q328 30 332 14 T348 44 T364 6 T380 52 T396 12 T412 46 T428 20 T444 30 H600 Q608 30 612 24 T628 34 T644 30 H820 Q830 30 836 10 T852 48 T868 8 T884 50 T900 16 T916 30 H1100 Q1108 30 1112 26 T1128 32 T1144 30 H1400";

const FINDINGS = [
  {
    index: "i",
    title: "Alpha power",
    body: "Significant increase in alpha wave power and coherence across all brain regions.",
  },
  {
    index: "ii",
    title: "Delta & theta",
    body: "Marked decrease in delta and theta power — correlating with inner light perception.",
  },
  {
    index: "iii",
    title: "Inner light",
    body: null, // rendered inline below — carries a SanskritTerm
  },
] as const;

/**
 * The evidence — findings set as a ruled table, the way results read
 * in a journal: numbered, hairline-separated, no decoration.
 */
export function Science() {
  const traceRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: traceRef,
    offset: ["start 0.95", "start 0.35"],
  });

  return (
    <section className="relative overflow-hidden bg-cream-dark px-7 py-24 md:py-32">
      <div className="mx-auto max-w-6xl">
        <SectionHead index="04" label="The Evidence" />

        {/* The trace — an alpha-wave line drawn across the page as the
            reader arrives, the way it draws across an EEG chart. */}
        <div ref={traceRef} className="mt-10 md:mt-12" aria-hidden>
          <svg
            className="w-full"
            viewBox="0 0 1400 60"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: 44 }}
          >
            <motion.path
              d={EEG_PATH}
              stroke="var(--color-gold)"
              strokeWidth="1.1"
              opacity="0.4"
              style={reduce ? undefined : { pathLength: scrollYProgress }}
            />
          </svg>
        </div>

        <div className="mt-14 grid gap-12 md:mt-20 md:grid-cols-12 md:gap-10">
          <ScrollReveal className="md:col-span-5">
            <h2 className="font-display text-3xl leading-[1.22] text-warm-brown md:text-[1.9rem]">
              This is not belief.
              <br />
              <span className="text-gold">This is repeatable experience.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-7">
            <p className="max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
              Neuroscientists at the Central Institute of Psychiatry (Ranchi,
              India) and the University of Bergamo (Italy) have studied{" "}
              <SanskritTerm
                term="Vihangam Yog"
                devanagari="विहंगम योग"
                pronunciation="vi-hung-um yog"
                meaning="The science of consciousness — the path of the soul that soars like a bird beyond the material world."
              />{" "}
              meditation using 192-channel EEG recordings.
            </p>
          </ScrollReveal>
        </div>

        {/* Findings — ruled rows, journal-style. */}
        <div className="mt-16 md:mt-20">
          {FINDINGS.map((finding, i) => (
            <ScrollReveal key={finding.title} delay={0.1 * i}>
              <div className="hairline-t grid gap-3 py-8 md:grid-cols-12 md:gap-10">
                <div className="flex items-baseline gap-4 md:col-span-4">
                  <span className="verse-index">{finding.index}</span>
                  <h3 className="text-lg font-medium text-warm-brown md:text-xl">
                    {finding.title}
                  </h3>
                </div>
                <p className="text-[15px] leading-relaxed text-warm-brown-light md:col-span-8 md:max-w-2xl md:text-base">
                  {finding.body ?? (
                    <>
                      Practitioners in{" "}
                      <SanskritTerm
                        term="Samādhi"
                        devanagari="समाधि"
                        pronunciation="suh-maa-dhi"
                        meaning="The state of union with the Supreme Being — the highest state of meditative consciousness."
                        lore="In Samādhi, the practitioner directly experiences the inner light — a state confirmed by EEG changes distinct from sleep, dreaming, or ordinary rest."
                      />{" "}
                      report perceiving an inner light throughout meditation —
                      confirmed by EEG data.
                    </>
                  )}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={0.3}>
          <p className="hairline-t max-w-xl pt-6 text-sm leading-relaxed text-warm-gray">
            State-specific EEG study by Dr. Ravi Prakash et al., Central
            Institute of Psychiatry, Ranchi. Research ongoing at institutions in
            India, Italy, Russia, and the USA.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
